/** Cached partner lookups so inbox/dashboard don't hammer `/user/user-info/:id` on deleted accounts or polling. */

import axios from "axios";

const MISSING = Symbol("partner_missing");

const partnerById = new Map();

/** Concurrent GET `/user/user-info/:id` collapsed to one in-flight request per partner id. */
const inflightPublicUserInfo = new Map();

/**
 * @returns {undefined} never fetched
 * @returns {null} fetched but user does not exist (404)
 * @returns {object} public shop/user payload
 */
export function getPartnerFromCache(partnerId) {
  if (partnerId == null || partnerId === "") return undefined;
  const key = String(partnerId);
  if (!partnerById.has(key)) return undefined;
  const v = partnerById.get(key);
  return v === MISSING ? null : v;
}

export function rememberPartner(partnerId, payload) {
  if (partnerId == null || partnerId === "") return;
  partnerById.set(String(partnerId), payload);
}

export function rememberPartnerMissing(partnerId) {
  if (partnerId == null || partnerId === "") return;
  partnerById.set(String(partnerId), MISSING);
}

export function fallbackPartnerProfile(partnerId) {
  return {
    _id: partnerId,
    username: "Unavailable",
    name: "Unavailable",
    avatar: null,
    isSeller: false,
  };
}

/**
 * Public user profile by id (same endpoint everywhere). Uses cache + single in-flight
 * request per id so list views / StrictMode don't duplicate 404s.
 * @returns user object or null (missing / 404); throws on non-404 HTTP/network errors.
 */
export async function fetchPublicUserInfoDeduped(apiBase, partnerId) {
  const pid = partnerId == null || partnerId === "" ? "" : String(partnerId);
  if (!pid) return null;

  const cached = getPartnerFromCache(pid);
  if (cached !== undefined) {
    return cached === null ? null : cached;
  }

  let pending = inflightPublicUserInfo.get(pid);
  if (pending) return pending;

  pending = (async () => {
    try {
      const r = await axios.get(`${apiBase}/user/user-info/${partnerId}`);
      const userInfo = r.data?.user;
      if (userInfo) {
        rememberPartner(pid, userInfo);
        return userInfo;
      }
      rememberPartnerMissing(pid);
      return null;
    } catch (e) {
      if (e?.response?.status === 404) {
        rememberPartnerMissing(pid);
        return null;
      }
      throw e;
    }
  })();

  inflightPublicUserInfo.set(pid, pending);
  pending.finally(() => inflightPublicUserInfo.delete(pid));

  return pending;
}
